import { useEffect, useState } from 'react';
import { adminApi, formatMoney } from '../api';

interface Period {
  month: string;
  cashInMicro: number;
  cashInByMethod: Record<string, number>;
  topups: number;
  bonusMicro: number;
  trialCreditMicro: number;
  referralCreditMicro: number;
  adjustmentMicro: number;
  refundMicro: number;
  recognisedMicro: number;
  recognisedByCategory: { storage: number; activity: number; network: number };
  usageDebitedMicro: number;
  trialExpiredMicro: number;
}
interface Finance {
  liability: { paidMicro: number; promotionalMicro: number; accounts: number; fundedAccounts: number };
  current: { month: string; cashInMicro: number; recognisedMicro: number; creditsIssuedMicro: number };
  periods: Period[];
}

const METHOD_LABEL: Record<string, string> = {
  card: 'card',
  us_bank_account: 'ACH',
  bank_transfer: 'bank transfer',
  'local-fake-checkout': 'test checkout',
};

/** Money in (top-ups), money recognised (usage), credits given away, and what we still owe customers. */
export function FinanceSection({ tick }: { tick: number }) {
  const [data, setData] = useState<Finance | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    adminApi<Finance>('/finance')
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, [tick]);
  if (error) return <p className="error">{error}</p>;
  if (!data) return null;
  const m = (v: number) => formatMoney(v, true);
  return (
    <div className="card">
      <h2>Finance</h2>
      <div className="grid cols-3" style={{ marginBottom: 14 }}>
        <div>
          <div className="stat">Cash in this month</div>
          <div className="stat-value">{m(data.current.cashInMicro)}</div>
          <div className="muted">top-ups, before Stripe fees</div>
        </div>
        <div>
          <div className="stat">Recognised this month</div>
          <div className="stat-value">{m(data.current.recognisedMicro)}</div>
          <div className="muted">usage billed to wallets · the revenue line</div>
        </div>
        <div>
          <div className="stat">Owed to customers</div>
          <div className="stat-value">{m(data.liability.paidMicro)}</div>
          <div className="muted">
            unspent prepaid across {data.liability.fundedAccounts} funded of {data.liability.accounts}{' '}
            accounts · {m(data.liability.promotionalMicro)} promo outstanding
          </div>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th>Cash in</th>
              <th>By method</th>
              <th>Recognised</th>
              <th>Storage · activity · transfer</th>
              <th>Credits issued</th>
              <th>Trial expired</th>
              <th>Adjust · refund</th>
            </tr>
          </thead>
          <tbody>
            {data.periods.map((p) => (
              <tr key={p.month}>
                <td>
                  <strong>{p.month}</strong>
                </td>
                <td>
                  {m(p.cashInMicro)} <span className="muted">({p.topups})</span>
                </td>
                <td className="muted">
                  {Object.entries(p.cashInByMethod)
                    .map(([k, v]) => `${METHOD_LABEL[k] ?? k} ${m(v)}`)
                    .join(' · ') || '—'}
                </td>
                <td>{m(p.recognisedMicro)}</td>
                <td className="muted">
                  {m(p.recognisedByCategory.storage)} · {m(p.recognisedByCategory.activity)} ·{' '}
                  {m(p.recognisedByCategory.network)}
                </td>
                <td>
                  {m(p.bonusMicro + p.trialCreditMicro + p.referralCreditMicro)}
                  <div className="muted">
                    trial {m(p.trialCreditMicro)} · bonus {m(p.bonusMicro)} · referral{' '}
                    {m(p.referralCreditMicro)}
                  </div>
                </td>
                <td className="muted">{m(p.trialExpiredMicro)}</td>
                <td className="muted">
                  {m(p.adjustmentMicro)} · {m(p.refundMicro)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
        Recognised comes from metered usage; cash in from top-ups. The gap between them, plus credits, is the
        prepaid float. Stripe fees are not deducted here.
      </p>
    </div>
  );
}
